import { compose } from 'ramda';

/**
 * Creates a store enhancer that applies middleware to the dispatch method
 * of the Redux store. This is handy for a variety of tasks, such as expressing
 * asynchronous actions in a concise manner, or logging every action payload.
 *
 * See `redux-thunk` package as an example of the Redux middleware.
 *
 * Because middleware is potentially asynchronous, this should be the first
 * store enhancer in the composition chain.
 *
 * Note that each middleware will be given the `dispatch` and `getState` functions
 * as named arguments.
 *
 * @param {...Function} middlewares The middleware chain to be applied.
 * @returns {Function} A store enhancer applying the middleware.
 */
export default function applyMiddleware(...middlewares) {
  return (createStore) => (reducer, initialState, enhancer) => {
    var store = createStore(reducer, initialState, enhancer);
    let { dispatch } = store;
    var chain = [];

    var middlewareAPI = {
      get: store.get,
      getIn: store.getIn,
      getState: store.getState,
      getTranslator: store.getTranslator,
      getDerived: store.getDerived,
      dispatch: (action) => dispatch(action),
      read: store.read,
      write: store.write,
      hasApi: store.hasApi,
      callApi: store.callApi,
      logPageView: store.logPageView,
      error: store.error,
    };

    chain = middlewares.map(middleware => middleware(middlewareAPI));
    dispatch = compose(...chain)(store.dispatch);

    return Object.assign({}, store, { dispatch });
  };
}



// WEBPACK FOOTER //
// ./src/store/applyMiddleware.js